// src/components/header/MobileMenu.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProductCategoriesWithSubs } from '@/hooks/useProductCategories';
import { useAuth } from '@/hooks/auth';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  categories?: {
    name: string;
    subcategories: string[];
  }[];
}

const MobileMenu = ({ isOpen, onClose, categories: propCategories }: MobileMenuProps) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);

  // Use either provided categories or import from data
  const { categories: dbCategories } = useProductCategoriesWithSubs();
  const menuCategories = propCategories || dbCategories.map(category => ({
    name: category.name,
    subcategories: (category.product_subcategories || []).map(sub => sub.name)
  }));

  const toggleDropdown = (index: number) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-[80%] max-w-[350px] bg-white z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-xl">Menu</h3>
            <button onClick={onClose} className="p-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m18 6-12 12"/>
                <path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 border-b">
          <div className="flex space-x-2">
            <Link
              to="/login"
              className="flex-1 py-2 px-3 bg-white border border-springbok-500 text-springbok-500 rounded-md text-center"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="flex-1 py-2 px-3 rounded-md text-center"
              data-gradient="green"
            >
              Register
            </Link>
          </div>
        </div>

        <nav className="p-4">
          <ul className="space-y-1">
            <li>
              <Link
                to="/"
                className="flex items-center space-x-2 p-3 rounded-md hover:bg-springbok-50"
                onClick={onClose}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9,22 9,12 15,12 15,22"/>
                </svg>
                <span>Home</span>
              </Link>
            </li>
            <li>
              <Link
                to="/account"
                className="flex items-center space-x-2 p-3 rounded-md hover:bg-springbok-50"
                onClick={onClose}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span>My Account</span>
              </Link>
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t">
          <h4 className="font-medium mb-2">Categories</h4>
          <ul className="space-y-1">
            {menuCategories.map((category, index) => (
              <li key={category.name} className="border-b border-gray-100 pb-1">
                <div className="flex items-center justify-between p-2">
                  <Link
                    to={`/products?category=${category.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                    className="flex-1 text-sm"
                    onClick={onClose}
                  >
                    {category.name}
                  </Link>
                  {category.subcategories.length > 0 && (
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="p-1"
                    >
                      <div className={`transition-transform duration-200 ${
                        activeDropdown === index ? 'transform rotate-180' : ''
                      }`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </div>
                    </button>
                  )}
                </div>
                {category.subcategories.length > 0 && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      activeDropdown === index ? 'max-h-60' : 'max-h-0'
                    }`}
                  >
                    <ul className="bg-cream-50 rounded-md my-1 py-1">
                      {category.subcategories.map((sub) => (
                        <li key={sub}>
                          <Link
                            to={`/products?category=${category.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}&subcategory=${sub.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                            className="block pl-4 py-2 text-xs hover:text-springbok-500"
                            onClick={onClose}
                          >
                            {sub}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 border-t">
          <ul className="space-y-2">
            <li>
              <Link to="/tools" className="block p-2 hover:text-springbok-500 text-sm" onClick={onClose}>
                Agri Tools & Calculators
              </Link>
            </li>
            <li>
              <Link to="/blog" className="block p-2 hover:text-springbok-500 text-sm" onClick={onClose}>
                Blog
              </Link>
            </li>
            <li>
              <Link to="/contact" className="block p-2 hover:text-springbok-500 text-sm" onClick={onClose}>
                Contact Us
              </Link>
            </li>
            {(isAdmin || isSuperAdmin) && (
              <li>
                <Link to="/test-delivery" className="block p-2 hover:text-springbok-500 text-sm" onClick={onClose}>
                  🧪 Test Delivery
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;